import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { getSortOptionFromURI } from "components/Filters/helpers";
import { EmptystateKeyColored, EmptystateSearchNoResultsColored } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper from "ds/components/Table/Wrapper";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrderDirection } from "types/generated";

import {
  SPACE_API_KEYS_FILTERS_ORDER_KEY,
  SPACE_API_KEYS_INITIAL_LIMIT,
  SPACE_API_KEYS_INITIAL_ORDER_BY,
  SPACE_API_KEYS_LIMIT_STORAGE_KEY,
  SPACE_API_KEYS_SORT_CONFIG,
} from "./constants";
import SpaceDetailsLoginPolicyCallout from "../components/LoginPolicyCallout";
import SpaceDetailsTableHead from "../components/TableHead";
import SpaceDetailsApiKeysRows from "./Rows";
import useSpaceDetailsApiKeys from "./useSpaceDetailsApiKeys";
import useSpaceDetailsApiKeysTableStore from "./useStore";

type SpaceDetailsApiKeysProps = {
  spaceId: string;
};

const TABLE_ARIA_LABEL = "Space API keys table";

const SpaceDetailsApiKeys = ({ spaceId }: SpaceDetailsApiKeysProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [storedRowsPerPage] = useLocalStorage<number>(
    SPACE_API_KEYS_LIMIT_STORAGE_KEY,
    SPACE_API_KEYS_INITIAL_LIMIT
  );

  const first = storedRowsPerPage || SPACE_API_KEYS_INITIAL_LIMIT;

  const fullTextSearch = getSearchQuery(searchParams) || null;
  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(
        searchParams,
        SPACE_API_KEYS_INITIAL_ORDER_BY.field,
        SearchQueryOrderDirection.Asc
      )[0] ?? SPACE_API_KEYS_INITIAL_ORDER_BY,
    [searchParams]
  );

  const columnIds = useSpaceDetailsApiKeysTableStore((state) => state.columnsOrder.visible);

  const {
    loadApiKeys,
    error,
    apiKeys,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor,
    startCursor,
    paginationResetKey,
  } = useSpaceDetailsApiKeys({ spaceId, orderBy, first, fullTextSearch });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadApiKeys,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: apiKeys.length,
    initialLimit: SPACE_API_KEYS_INITIAL_LIMIT,
    storageKey: SPACE_API_KEYS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLayoutSkeleton />;
  }

  return (
    <>
      <PageInfoHeaderWrapper>
        <PageInfoHeaderTitle title="API Keys" />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search API keys"
            filtersOrderSettingsKey={SPACE_API_KEYS_FILTERS_ORDER_KEY}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <SpaceDetailsLoginPolicyCallout />

      <ListContentWrapper>
        {!apiKeys.length && !fullTextSearch && (
          <EmptyState
            title="This space has no API keys yet"
            icon={EmptystateKeyColored}
            caption="API keys with access to this space will show up here."
          />
        )}

        {!apiKeys.length && !!fullTextSearch && (
          <EmptyState
            title="No API keys found"
            icon={EmptystateSearchNoResultsColored}
            caption="No API keys match your search."
          />
        )}

        {!!apiKeys.length && (
          <>
            <TableWrapper ref={tableRef} hasPagination>
              <Table ariaLabel={TABLE_ARIA_LABEL}>
                <SpaceDetailsTableHead
                  store={useSpaceDetailsApiKeysTableStore}
                  tableRef={tableRef}
                  sortConfig={SPACE_API_KEYS_SORT_CONFIG}
                  filtersStorageKey={SPACE_API_KEYS_FILTERS_ORDER_KEY}
                />
                {loadingContent && (
                  <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />
                )}
                {!loadingContent && (
                  <TableBody>
                    <SpaceDetailsApiKeysRows
                      data={apiKeys}
                      store={useSpaceDetailsApiKeysTableStore}
                      tableRef={tableRef}
                    />
                  </TableBody>
                )}
              </Table>
            </TableWrapper>
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </ListContentWrapper>
    </>
  );
};

export default SpaceDetailsApiKeys;
